import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail, Send, ArrowLeft, Loader2, Save, Plus, Trash2, Globe, Info, CheckCircle2,
  GripVertical, Star,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { getTranslations } from '../../i18n';

// WYSIWYG editor for the public Contact page. Renders the page exactly as a
// visitor sees it, but every text + the "Asunto" dropdown options (with their
// destination emails) are editable inline. Saves to /admin/contact/config.
export default function AdminContactEditorPage() {
  const [lang, setLang] = useState('es');           // which locale is being edited/previewed
  const [page, setPage] = useState({});             // contact_page overrides
  const [routes, setRoutes] = useState([]);         // subject → email routes
  const [inbox, setInbox] = useState('');           // default inbox
  const [fallback, setFallback] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);

  const base = getTranslations(lang).contact;       // i18n fallback for placeholders
  const suffix = lang === 'en' ? '_en' : '_es';

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/contact/config');
        setPage(data.contact_page || {});
        setRoutes(data.contact_routes || []);
        setInbox(data.contact_inbox_email || '');
        setFallback(data.default_inbox_fallback || '');
      } catch (e) {
        setFeedback({ type: 'error', message: formatApiError(e.response?.data?.detail) || 'Error al cargar' });
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (feedback?.type === 'success') {
      const t = setTimeout(() => setFeedback(null), 3000);
      return () => clearTimeout(t);
    }
  }, [feedback]);

  const setField = (key, val) => setPage((p) => ({ ...p, [key]: val }));
  const val = (key) => page[`${key}${suffix}`] || '';

  function addRoute() {
    setRoutes((r) => [...r, { id: `new-${Date.now()}`, label_es: '', label_en: '', email: '' }]);
  }
  function updateRoute(i, field, v) {
    setRoutes((r) => r.map((row, idx) => (idx === i ? { ...row, [field]: v } : row)));
  }
  function removeRoute(i) {
    setRoutes((r) => r.filter((_, idx) => idx !== i));
  }
  function moveRoute(from, to) {
    if (from == null || to == null || from === to) return;
    setRoutes((r) => {
      const arr = [...r];
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      return arr;
    });
  }
  function setDefault(i) {
    setRoutes((r) => r.map((row, idx) => ({ ...row, is_default: idx === i ? !row.is_default : false })));
  }

  async function save() {
    setSaving(true); setFeedback(null);
    try {
      const cleanRoutes = routes.filter((r) => (r.label_es || r.label_en));
      await api.post('/admin/contact/config', {
        contact_inbox_email: inbox.trim(),
        contact_routes: cleanRoutes,
        contact_page: page,
      });
      setRoutes(cleanRoutes);
      setFeedback({ type: 'success', message: 'Cambios guardados. Así se verá la página de contacto.' });
    } catch (e) {
      setFeedback({ type: 'error', message: formatApiError(e.response?.data?.detail) || 'No se pudo guardar' });
    } finally { setSaving(false); }
  }

  if (loading) {
    return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-cmic-500" size={30} /></div>;
  }

  // Small styled editable input used over the hero (white text on gradient).
  const HeroInput = (props) => (
    <input
      {...props}
      className={`bg-white/10 border border-white/30 rounded-lg px-3 py-1.5 text-center focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/20 placeholder-white/50 ${props.className || ''}`}
    />
  );

  return (
    <div data-testid="admin-contact-editor-page" className="space-y-6 max-w-5xl mx-auto pb-28">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <Link to="/admin/contact" data-testid="contact-editor-back" className="inline-flex items-center gap-1.5 text-sm text-charcoal-500 hover:text-cmic-600 mb-1">
            <ArrowLeft size={14} /> Volver a mensajes
          </Link>
          <h1 className="text-2xl font-bold text-charcoal-800">Editor del formulario de contacto</h1>
          <p className="text-charcoal-500 text-sm mt-0.5">Edita los textos y los temas del formulario. Lo que ves aquí es exactamente lo que verá el visitante.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white overflow-hidden" data-testid="contact-editor-lang-toggle">
            <button onClick={() => setLang('es')} data-testid="contact-editor-lang-es"
              className={`px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${lang === 'es' ? 'bg-cmic-500 text-white' : 'text-charcoal-600 hover:bg-gray-50'}`}>
              <Globe size={12} /> ES
            </button>
            <button onClick={() => setLang('en')} data-testid="contact-editor-lang-en"
              className={`px-3 py-1.5 text-xs font-semibold ${lang === 'en' ? 'bg-cmic-500 text-white' : 'text-charcoal-600 hover:bg-gray-50'}`}>
              EN
            </button>
          </div>
        </div>
      </div>

      {feedback && (
        <div data-testid="contact-editor-feedback" className={`px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 ${feedback.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {feedback.type === 'success' && <CheckCircle2 size={16} />}{feedback.message}
        </div>
      )}

      <p className="text-xs text-charcoal-500 bg-cmic-50/70 border border-cmic-100 rounded-lg p-3 flex items-start gap-2">
        <Info size={14} className="mt-0.5 flex-shrink-0 text-cmic-500" />
        <span>Los campos con borde punteado son editables. Cambia el idioma (ES/EN) para editar cada versión. Edita los <strong>temas del formulario</strong> abajo para definir a qué correo llega cada mensaje según el asunto elegido.</span>
      </p>

      {/* ── WYSIWYG preview ─────────────────────────────────────────── */}
      <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm bg-white">
        {/* Hero */}
        <section className="relative py-12 px-6 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-cmic-800 via-cmic-700 to-cmic-600" />
          <div className="relative max-w-2xl mx-auto text-center flex flex-col items-center gap-3">
            <HeroInput value={val('label')} onChange={(e) => setField(`label${suffix}`, e.target.value)}
              data-testid="contact-editor-label" placeholder={base.label}
              className="text-xs font-semibold text-cmic-100 uppercase tracking-wider w-48" />
            <HeroInput value={val('title')} onChange={(e) => setField(`title${suffix}`, e.target.value)}
              data-testid="contact-editor-title" placeholder={base.title}
              className="text-2xl sm:text-3xl font-bold text-white w-full max-w-md" />
            <textarea value={val('subtitle')} onChange={(e) => setField(`subtitle${suffix}`, e.target.value)}
              data-testid="contact-editor-subtitle" placeholder={base.subtitle} rows={2}
              className="bg-white/10 border border-white/30 rounded-lg px-3 py-1.5 text-center text-sm text-cmic-100 w-full max-w-lg resize-none focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/20 placeholder-white/50" />
          </div>
        </section>

        {/* Body */}
        <div className="p-6 sm:p-8">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left: contact info (editable) */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <input value={val('info_title')} onChange={(e) => setField(`info_title${suffix}`, e.target.value)}
                  data-testid="contact-editor-info-title" placeholder={base.infoTitle}
                  className="text-xl font-semibold text-charcoal-800 mb-3 w-full border border-dashed border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-400" />
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-cmic-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail size={20} className="text-cmic-500" />
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <input value={val('email_label')} onChange={(e) => setField(`email_label${suffix}`, e.target.value)}
                      data-testid="contact-editor-email-label" placeholder={base.emailLabel}
                      className="text-sm text-charcoal-400 w-full border border-dashed border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-400" />
                    <input value={page.contact_email || ''} onChange={(e) => setField('contact_email', e.target.value)}
                      data-testid="contact-editor-contact-email" placeholder="cmic@cmicmexico.org"
                      className="text-cmic-600 font-medium w-full border border-dashed border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-400" />
                  </div>
                </div>
              </div>
              <div className="bg-cmic-50 rounded-xl p-4">
                <textarea value={val('info_note')} onChange={(e) => setField(`info_note${suffix}`, e.target.value)}
                  data-testid="contact-editor-info-note" placeholder={base.infoNote} rows={4}
                  className="text-sm text-charcoal-600 leading-relaxed w-full bg-transparent border border-dashed border-cmic-200 rounded-md px-2 py-1.5 resize-y focus:outline-none focus:ring-2 focus:ring-cmic-500/30" />
              </div>
            </div>

            {/* Right: the form (subject dropdown is the editable part) */}
            <div className="lg:col-span-3">
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">{base.form.name}</label>
                    <div className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-charcoal-300 text-sm select-none">…</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">{base.form.email}</label>
                    <div className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-charcoal-300 text-sm select-none">…</div>
                  </div>
                </div>

                {/* Subject = the editable dropdown options + their destination emails */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">{base.form.subject}</label>
                  <div className="rounded-xl border-2 border-dashed border-cmic-300 bg-white p-3 space-y-2" data-testid="contact-editor-routes">
                    <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-charcoal-400 px-1">
                      <span className="w-4" />
                      <span className="flex-1">Opción (ES)</span>
                      <span className="flex-1">Opción (EN)</span>
                      <span className="flex-[1.4]">Correo destino</span>
                      <span className="w-16 text-center">Predet.</span>
                    </div>
                    {routes.length === 0 ? (
                      <p className="text-xs text-charcoal-400 italic py-2 px-1">Sin opciones. El formulario mostrará un campo de asunto libre y todo llegará al correo por defecto.</p>
                    ) : routes.map((r, i) => (
                      <div key={r.id || i}
                        draggable
                        onDragStart={() => setDragIndex(i)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => { moveRoute(dragIndex, i); setDragIndex(null); }}
                        onDragEnd={() => setDragIndex(null)}
                        className={`flex items-center gap-2 rounded-md transition-opacity ${dragIndex === i ? 'opacity-40' : ''}`}
                        data-testid={`contact-editor-route-${i}`}>
                        <span data-testid={`contact-editor-drag-${i}`} title="Arrastra para reordenar"
                          className="cursor-grab active:cursor-grabbing text-charcoal-300 hover:text-charcoal-500 flex-shrink-0">
                          <GripVertical size={15} />
                        </span>
                        <input value={r.label_es} onChange={(e) => updateRoute(i, 'label_es', e.target.value)} placeholder="Membresía"
                          data-testid={`contact-editor-label-es-${i}`}
                          className="flex-1 min-w-0 px-2.5 py-2 rounded-md border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-cmic-500/30" />
                        <input value={r.label_en} onChange={(e) => updateRoute(i, 'label_en', e.target.value)} placeholder="Membership"
                          className="flex-1 min-w-0 px-2.5 py-2 rounded-md border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-cmic-500/30" />
                        <input type="email" value={r.email} onChange={(e) => updateRoute(i, 'email', e.target.value)} placeholder="membresia@cmicmexico.org"
                          data-testid={`contact-editor-email-${i}`}
                          className="flex-[1.4] min-w-0 px-2.5 py-2 rounded-md border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-cmic-500/30" />
                        <button onClick={() => setDefault(i)} data-testid={`contact-editor-default-${i}`}
                          title={r.is_default ? 'Opción predeterminada (preseleccionada en el formulario)' : 'Marcar como predeterminada'}
                          className={`w-7 flex items-center justify-center flex-shrink-0 ${r.is_default ? 'text-amber-500' : 'text-charcoal-300 hover:text-amber-500'}`}>
                          <Star size={15} fill={r.is_default ? 'currentColor' : 'none'} />
                        </button>
                        <button onClick={() => removeRoute(i)} data-testid={`contact-editor-remove-${i}`}
                          className="w-7 flex items-center justify-center flex-shrink-0 text-charcoal-400 hover:text-rose-600">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                    <button onClick={addRoute} data-testid="contact-editor-add-route"
                      className="inline-flex items-center gap-1 text-xs font-medium text-cmic-700 hover:bg-cmic-50 rounded-md px-2 py-1.5 border border-cmic-200 mt-1">
                      <Plus size={13} /> Agregar opción de asunto
                    </button>
                    <p className="text-[11px] text-charcoal-400 pt-1 flex items-center gap-1.5">
                      <GripVertical size={11} /> Arrastra para reordenar · <Star size={11} /> marca la opción que aparece preseleccionada.
                    </p>
                  </div>
                  <div className="mt-2">
                    <label className="block text-xs font-medium text-charcoal-600 mb-1">Correo por defecto (cuando el asunto no tiene uno asignado)</label>
                    <input type="email" value={inbox} onChange={(e) => setInbox(e.target.value)} data-testid="contact-editor-inbox"
                      placeholder={fallback || 'cmic@cmicmexico.org'}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">{base.form.message}</label>
                  <div className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-charcoal-300 text-sm h-28 select-none">…</div>
                </div>

                <button type="button" disabled
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-cmic-500 text-white px-8 py-3 rounded-lg font-semibold opacity-80 cursor-default">
                  <Send size={18} />{base.form.send}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-white border-t border-gray-200 px-4 sm:px-8 py-3 flex items-center justify-end gap-3 z-30">
        <Link to="/admin/contact" className="px-4 py-2 rounded-lg text-charcoal-600 hover:bg-gray-100 text-sm">Cancelar</Link>
        <button onClick={save} disabled={saving} data-testid="contact-editor-save"
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-cmic-600 text-white font-medium text-sm hover:bg-cmic-700 disabled:opacity-50">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar cambios
        </button>
      </div>
    </div>
  );
}
